import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';
import { SectionShell } from './SectionShell';
import { SectionHeader } from './SectionHeader';
import { CARD_ENTRANCE, staggerStyle } from '../../lib/animation';
import { PERSONAS } from './personas';
import { PersonaIcon, MagnifierHint } from './PersonaIcon';

/**
 * Returns true when the user has asked the OS for reduced motion.
 * Used to suppress the accordion's inline flex-grow easing — the
 * layout still snaps to the new sizes, just without the 420ms
 * cubic-bezier sweep. SSR-safe.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// Constant card height for the desktop expanding row. Picked so the
// active card has enough room to show icon + title + description + quote
// without scroll, while compressed cards still look proportionate. If
// content changes meaningfully, bump this rather than letting cards
// reflow vertically — the spec calls for fixed height during hover.
const ROW_HEIGHT = 240;

// flex-grow ratios — total = 9 + 1 + 1 + 1 = 12, so the active card
// lands at 9/12 ≈ 75% and each compressed card at 1/12 ≈ 8.3%.
const ACTIVE_GROW = 9;
const RESTING_GROW = 1;

/**
 * "03 · Who Is This For?" — four persona cards (Researchers,
 * Policymakers, Institutional Leaders, Educators & Counselors).
 *
 * Desktop (lg+) interaction model — horizontal expanding accordion:
 *   - Default: all 4 cards equal width, each showing icon + title.
 *   - Hover (or keyboard focus) on a card: that card grows to ~75% of
 *     the row, the other 3 compress and share the remaining ~25%.
 *   - Equal height across all 4 cards (set on the parent), no vertical
 *     reflow during interaction.
 *   - Compressed siblings show icon + vertically-rotated title so the
 *     headline stays readable even at ~70px wide.
 *   - Active card reveals full description + reflection quote.
 *
 * Below lg the expanding behavior is disabled and cards stack as a
 * standard responsive grid with all content visible at rest.
 *
 * Per-card CTAs were removed in the 2026-05-18 feedback pass; a single
 * pair of "Start with Rankings / Start with Map" CTAs lives below the
 * card grid for the whole section.
 */
export function WhoItsForSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <SectionShell id="who-its-for" variant="alt">
      <SectionHeader
        eyebrow="03 · Who Is This For?"
        headline={
          <>
            Built for the people shaping
            <br />
            workforce outcomes
          </>
        }
        lead="Whether you're analyzing trends, making policy, leading an institution, or guiding learners, this tool gives you the data you need."
      />

      {/* Mobile / tablet (< lg): stacked responsive grid, full content
          visible at rest — no hover dependency. */}
      <ul
        className="grid gap-4 lg:hidden"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
      >
        {PERSONAS.map((persona, idx) => (
          <li
            key={persona.title}
            // A11y (WCAG 1.3.1): no role="group" here — it overrides the <li>'s
            // implicit listitem role, leaving the <ul> with no list items (axe
            // `list`). The <li> already IS the group; aria-label is valid on it.
            aria-label={persona.title}
            className={`relative flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-card ${CARD_ENTRANCE}`}
            style={staggerStyle(idx)}
          >
            <div className={`h-[3px] w-full ${persona.stripeClass}`} aria-hidden="true" />
            <div className="flex flex-1 flex-col gap-3 p-5">
              <PersonaIcon persona={persona} />
              <h3 className="text-card-title font-bold text-text-1">{persona.title}</h3>
              {/* CR-022: body copy raised text-[13px] → text-base (16px). */}
              <p className="text-base leading-[1.7] text-text-3">{persona.desc}</p>
              {/* CR-022: pull-quote raised text-[12.5px] → text-base (16px);
                  italic + border + ice fill keep it visually distinct. */}
              <blockquote className="mt-auto rounded-r-sm border-l-[3px] border-blue bg-ice px-3.5 py-2.5 text-base italic leading-[1.6] text-text-2">
                {persona.quote}
              </blockquote>
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop (lg+): horizontal expanding accordion */}
      <ul
        className="hidden gap-3 lg:flex lg:items-stretch"
        style={{ height: ROW_HEIGHT }}
        onMouseLeave={() => setActiveIdx(null)}
      >
        {PERSONAS.map((persona, idx) => {
          const isActive = activeIdx === idx;
          const isAnyActive = activeIdx !== null;
          const isCompressed = isAnyActive && !isActive;

          return (
            <li
              key={persona.title}
              tabIndex={0}
              // A11y (WCAG 1.3.1): see the mobile list above — role="group" on an
              // <li> breaks the list semantics (axe `list`). listitem + aria-label
              // is valid and keeps each card's accessible name.
              aria-label={persona.title}
              onMouseEnter={() => setActiveIdx(idx)}
              onFocus={() => setActiveIdx(idx)}
              onBlur={(e) => {
                // Only clear if focus actually left the card — moving
                // focus to a child element should keep the card active.
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setActiveIdx(null);
                }
              }}
              style={{
                flexGrow: isActive ? ACTIVE_GROW : RESTING_GROW,
                flexBasis: 0,
                flexShrink: 1,
                transition:
                  // The Tailwind motion-reduce variant doesn't apply to
                  // inline-style transitions, so we suppress the
                  // accordion easing entirely when prefers-reduced-motion
                  // is set. Layout still snaps to the new flex-grow
                  // value instantly — no animation, no jitter.
                  prefersReducedMotion()
                    ? 'none'
                    : 'flex-grow 420ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 240ms ease',
                ...staggerStyle(idx),
              }}
              className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-white shadow-card hover:shadow-[0_10px_28px_-8px_rgba(20,65,117,0.22)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40 ${CARD_ENTRANCE}`}
            >
              <div
                className={`h-[3px] w-full flex-none ${persona.stripeClass}`}
                aria-hidden="true"
              />

              {/* === Layer A: default resting state ===
                  Shown when NO card is active. Icon + horizontal title
                  centered on the card. */}
              <div
                className={`absolute inset-x-0 bottom-0 top-[3px] flex flex-col items-center justify-center gap-4 px-4 text-center transition-opacity duration-300 motion-reduce:transition-none ${
                  !isAnyActive ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
              >
                <PersonaIcon persona={persona} />
                <h3 className="inline-flex items-center gap-1.5 text-card-title font-bold text-text-1">
                  {persona.title}
                  <MagnifierHint />
                </h3>
              </div>

              {/* === Layer B: compressed sibling state ===
                  Shown when ANOTHER card is active. Just the icon,
                  centered vertically + horizontally. No title (cards
                  are too narrow at ~70px to read it cleanly). */}
              <div
                className={`absolute inset-x-0 bottom-0 top-[3px] flex items-center justify-center transition-opacity duration-300 motion-reduce:transition-none ${
                  isCompressed ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
              >
                <PersonaIcon persona={persona} />
              </div>

              {/* === Layer C: active state ===
                  Shown when THIS card is active. Icon stacked above
                  the title, then description, then reflection quote
                  pinned to the bottom. Spacing matches the reference
                  sample: icon-to-title tight, title-to-desc tight,
                  desc-to-quote generous (mt-auto pushes the quote
                  toward the bottom). */}
              <div
                className={`absolute inset-x-0 bottom-0 top-[3px] flex flex-col px-6 pt-4 pb-4 transition-opacity duration-300 motion-reduce:transition-none ${
                  isActive
                    ? 'opacity-100 delay-100'
                    : 'pointer-events-none opacity-0'
                }`}
              >
                <PersonaIcon persona={persona} />
                <h3 className="mt-3 text-card-title font-bold text-text-1">
                  {persona.title}
                </h3>
                {/* CR-022: body copy raised text-[13px] → text-base (16px). */}
                <p className="mt-2 text-base leading-[1.6] text-text-3">
                  {persona.desc}
                </p>
                {/* `mt-auto` pushes the quote to the bottom of the
                    card so it lands at the same Y-position no matter
                    which persona is active (descriptions vary in
                    length, but the quote is always flush with the
                    bottom edge). */}
                {/* CR-022: pull-quote raised text-[12.5px] → text-base (16px). */}
                <blockquote className="mt-auto rounded-r-sm border-l-[3px] border-blue bg-ice px-3.5 py-2.5 text-base italic leading-[1.5] text-text-2">
                  {persona.quote}
                </blockquote>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Single pair of bottom CTAs — both routes are V1-live so neither
          needs the disabled-placeholder treatment. */}
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <Link
          to={ROUTES.rankings}
          className="inline-flex items-center justify-center rounded-pill border border-blue/20 bg-blue px-7 py-[13px] text-sm font-bold text-white shadow-[0_4px_16px_rgba(0,116,204,0.28)] transition hover:brightness-110 hover:-translate-y-px"
        >
          Start with Rankings
        </Link>
        <Link
          to={ROUTES.map}
          className="inline-flex items-center justify-center rounded-pill border border-navy/15 bg-white px-7 py-[13px] text-sm font-bold text-navy transition hover:bg-ice hover:-translate-y-px"
        >
          Start with Map
        </Link>
      </div>
    </SectionShell>
  );
}
